import { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Garde-fou global : capture toute erreur de rendu d'un composant enfant pour
 * éviter l'écran blanc total. Affiche un écran de secours et remonte l'erreur
 * à Sentry. Sans ça, un bug isolé dans une page fait planter toute l'app.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary a capturé une erreur:', error, errorInfo);
    try {
      Sentry.captureException(error, {
        extra: { componentStack: errorInfo.componentStack },
      });
    } catch {
      /* noop */
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#0a0a0a',
          color: '#fff',
          textAlign: 'center',
          padding: '24px',
          fontFamily: 'sans-serif',
        }}
      >
        <div style={{ fontSize: '28px', fontWeight: 900, letterSpacing: '-0.02em', marginBottom: '4px' }}>
          <span>Fest</span>
          <span style={{ color: '#4C3AFA' }}>in</span>
          <span style={{ color: '#FF5C00' }}>.</span>
        </div>
        <h1 style={{ fontSize: '20px', fontWeight: 700, margin: '16px 0 8px' }}>
          Oups, une erreur est survenue
        </h1>
        <p style={{ color: '#a1a1aa', maxWidth: '380px', lineHeight: 1.6, marginBottom: '24px' }}>
          Quelque chose s'est mal passé de notre côté. Nos équipes ont été notifiées
          automatiquement. Rechargez la page pour reprendre.
        </p>
        <button
          onClick={() => { window.location.href = '/'; }}
          style={{
            background: '#ff5a00',
            color: '#fff',
            border: 'none',
            padding: '12px 28px',
            borderRadius: '12px',
            fontWeight: 700,
            cursor: 'pointer',
            fontSize: '15px',
          }}
        >
          Recharger Festin
        </button>
      </div>
    );
  }
}
